import { Descriptions, Divider, Grid } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type InsuranceModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/InsuranceModuleWithPermissionsSpecs';
import { useListInsurersQuery } from '../../../../api/queries/listInsurers';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useListReducer from '../../InsurerListPage/useListReducer';
import InsuranceModuleActions from './InsuranceModuleActions';
import InsuranceModuleEmptyList from './InsuranceModuleEmptyList';
import InsuranceModuleTable from './InsuranceModuleTable';

export type InsuranceModulePageProps = { module: InsuranceModuleWithPermissionsSpecsFragment };

const InsuranceModulePage = ({ module }: InsuranceModulePageProps) => {
    const { t } = useTranslation(['insuranceModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();

    const [state, dispatch] = useListReducer();
    const { page, pageSize, sort } = state;

    const { data } = useListInsurersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { moduleId: module.id },
        },
    });

    // prepare items as data source
    const dataSource = useMemo(() => (data?.list?.items || []).map(item => ({ ...item, key: item.id })), [data]);

    const total = data?.list?.count || 0;

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('insuranceModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('insuranceModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<InsuranceModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('insuranceModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <Divider>{t('insuranceModuleDetails:dividers.insurers')}</Divider>
            {total === 0 ? (
                <InsuranceModuleEmptyList />
            ) : (
                <InsuranceModuleTable
                    dataSource={dataSource}
                    dispatch={dispatch}
                    module={module}
                    state={state}
                    total={total}
                />
            )}
        </ConsolePageWithHeader>
    );
};

export default InsuranceModulePage;
